import { useQueryClient } from '@tanstack/react-query'
import { Languages, Plus } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { COLECCIONES_POR_IDIOMA, type CodigoDeIdioma, NOMBRE_DE_IDIOMA, useIdiomas, useVersiones } from '../api/idiomas'
import { ErrorDeLaApi, pedir } from '../api/nucleo'
import { Aviso, Boton, Pastilla } from '../componentes/ui'

/**
 * Las versiones de un documento en los idiomas de la web (CLAUDE.md §37), encima del editor: a cuál se va,
 * «Crear versión en inglés» para las que faltan, y el aviso de que la española cambió después de revisar esta.
 * No se traduce nada: la versión nueva es una copia en borrador para escribirla en ese idioma.
 *
 * Con un solo idioma en la web no se pinta nada, salvo que el documento sea de otro idioma (uno apagado).
 */
export const VersionesEnIdiomas = ({ base, coleccion, id, sitio, sucio }: { base: string; coleccion: string; id: null | number; sitio: null | number; sucio?: boolean }) => {
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const aplica = COLECCIONES_POR_IDIOMA.has(coleccion) && sitio !== null && id !== null
  const { data: idiomas } = useIdiomas(aplica ? sitio : null)
  const { data, refetch } = useVersiones(aplica ? sitio : null, coleccion, aplica ? id : null)
  const [ocupado, setOcupado] = useState<null | string>(null)
  const [fallo, setFallo] = useState<null | string>(null)

  if (!aplica || !data || !idiomas) return null
  if (!data.varios && data.idioma === 'es') return null

  const crear = async (idioma: CodigoDeIdioma) => {
    setOcupado(idioma)
    setFallo(null)
    try {
      const { id: nuevo } = await pedir<{ id: number }>('/panel/traducciones/crear', { metodo: 'POST', cuerpo: { sitio, coleccion, id, idioma } })
      await clienteDeConsultas.invalidateQueries({ queryKey: ['lista', coleccion] })
      navegar(`${base}/${nuevo}`)
    } catch (e) {
      setFallo(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido crear la versión. Vuelve a intentarlo.')
    } finally {
      setOcupado(null)
    }
  }

  const alDia = async () => {
    setOcupado('al-dia')
    setFallo(null)
    try {
      await pedir('/panel/traducciones/al-dia', { metodo: 'POST', cuerpo: { sitio, coleccion, id } })
      await refetch()
    } catch (e) {
      setFallo(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido guardar. Vuelve a intentarlo.')
    } finally {
      setOcupado(null)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="inline-flex items-center gap-1.5 text-tenue">
          <Languages aria-hidden className="size-4" /> Idiomas:
        </span>
        {data.versiones.map((v) =>
          v.id === id ? (
            <span className="inline-flex items-center gap-1.5 rounded-pastilla border border-violeta bg-violeta-suave px-2.5 py-0.5 font-medium text-violeta" key={v.idioma}>
              {NOMBRE_DE_IDIOMA[v.idioma]} <span className="font-normal">(esta)</span>
            </span>
          ) : v.id !== null ? (
            <Link className="inline-flex items-center gap-1.5 rounded-pastilla border border-linea px-2.5 py-0.5 hover:border-violeta hover:text-violeta" key={v.idioma} title={v.titulo ?? undefined} to={`${base}/${v.id}`}>
              {NOMBRE_DE_IDIOMA[v.idioma]}
              {v.estado === 'borrador' && <Pastilla clave="sin-publicar">Borrador</Pastilla>}
              {v.desactualizada && <Pastilla clave="fallida">Desactualizada</Pastilla>}
            </Link>
          ) : idiomas.encendidos.includes(v.idioma) ? (
            <Boton cargando={ocupado === v.idioma} disabled={Boolean(ocupado) || sucio} icono={<Plus />} key={v.idioma} onClick={() => void crear(v.idioma)} pequeno title={sucio ? 'Guarda antes los cambios: la versión nueva se copia de lo guardado.' : undefined}>
              Crear versión en {NOMBRE_DE_IDIOMA[v.idioma].toLowerCase()}
            </Boton>
          ) : null,
        )}
      </div>
      {data.desactualizada && (
        <Aviso tono="aviso">
          <span className="flex flex-wrap items-center justify-between gap-3">
            <span>La versión en español se publicó con cambios después de la última vez que se revisó esta. Repasa los textos; si ya está bien, márcala como al día.</span>
            <Boton cargando={ocupado === 'al-dia'} disabled={Boolean(ocupado)} onClick={() => void alDia()} pequeno>
              Ya está al día
            </Boton>
          </span>
        </Aviso>
      )}
      {fallo && <Aviso tono="error">{fallo}</Aviso>}
    </div>
  )
}
